import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import Swal from 'sweetalert2';
import ExtraMileHeader from '../../components/ExtraMileHeader/ExtraMileHeader';
import { loginUser, logout } from '../../store/authSlice';
import { setBackButtonUrl } from '../../store/uiSlice';
import { apiPost } from '../../utils/apiClient';
import './login.css';

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== 'undefined' ? window.location.origin : '/') ||
    '/';
  if (!url || typeof url !== 'string') return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

export default function LoginPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { status, user, error } = useSelector((s) => s.auth);
  const videoRef = useRef(null);
  const wentWelcomeRef = useRef(false);
  const alreadyPlayedHandledRef = useRef(false);
  const [gate, setGate] = useState('pending'); // pending | play | blocked
  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    const otp = searchParams.get('otp');
    const demo = searchParams.get('demo');
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (status === 'succeeded' && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== 'failed') return;
    const msg = error ? String(error) : 'Session expired';
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes('OTP is required') || msg.includes('No saved user')
        ? 'Login required'
        : 'Session Expired!';
    Swal.fire({
      title,
      text: msg,
      icon: 'error',
      confirmButtonText: 'Close',
      customClass: {
        confirmButton: 'hf-swal-close',
        popup: 'hf-swal-popup',
      },
      buttonsStyling: false,
    }).then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  // Same user + session: if all 4 stages already played → popup → base URL
  useEffect(() => {
    if (status !== 'succeeded' || !user?.userId) return;
    if (alreadyPlayedHandledRef.current) return;

    let cancelled = false;
    (async () => {
      try {
        const res = await apiPost('/game/sessionStatus', {
          userId: user.userId,
        });
        if (cancelled) return;

        if (res.completed || res.alreadyPlayed) {
          alreadyPlayedHandledRef.current = true;
          setGate('blocked');
          const redirect = safeRedirectUrl(user.backButtonRedirect);
          await Swal.fire({
            title: 'You have already played!',
            icon: 'error',
            confirmButtonText: 'OK',
            allowOutsideClick: false,
            customClass: {
              confirmButton: 'hf-swal-close',
              popup: 'hf-swal-popup',
            },
            buttonsStyling: false,
          });
          window.location.href = redirect;
          return;
        }
      } catch (err) {
        console.error('sessionStatus check failed', err);
      }
      if (!cancelled) setGate('play');
    })();

    return () => {
      cancelled = true;
    };
  }, [status, user]);

  useEffect(() => {
    if (gate !== 'play') setVideoFailed(false);
  }, [gate]);

  // Keep intro video on screen; only go to Welcome when it finishes.
  useEffect(() => {
    if (gate !== 'play' || status !== 'succeeded' || !user || videoFailed) return;
    const video = videoRef.current;
    if (!video) return;

    wentWelcomeRef.current = false;
    const goWelcome = () => {
      if (wentWelcomeRef.current) return;
      wentWelcomeRef.current = true;
      navigate('/welcome', { replace: true });
    };

    const tryPlay = () => {
      const p = video.play();
      if (p && typeof p.catch === 'function') {
        p.catch(() => {
          /* keep video visible */
        });
      }
    };

    const onEnded = () => goWelcome();
    const onCanPlay = () => tryPlay();
    const onError = () => {
      setVideoFailed(true);
      window.setTimeout(goWelcome, 3000);
    };

    video.addEventListener('ended', onEnded);
    video.addEventListener('canplay', onCanPlay);
    video.addEventListener('error', onError);
    tryPlay();

    return () => {
      video.removeEventListener('ended', onEnded);
      video.removeEventListener('canplay', onCanPlay);
      video.removeEventListener('error', onError);
    };
  }, [gate, status, user, navigate, videoFailed]);

  const shell = (body) => (
    <div className="login-shell">
      <ExtraMileHeader />
      <div
        className="login-page login-splash-bg"
        style={{
          '--ic-bg': 'url(/img/MAINBG_Blur.jpg)',
          '--ic-bg-mob': 'url(/img/mainbgmob.png)',
        }}
      >
        {body}
      </div>
    </div>
  );

  if (status === 'loading' || status === 'idle' || (status === 'succeeded' && gate === 'pending')) {
    return shell(<div className="login-loader" />);
  }

  if (status === 'succeeded' && user && gate === 'blocked') {
    return shell(<div className="login-loader" />);
  }

  if (status === 'succeeded' && user && gate === 'play') {
    const introSrc = `${process.env.PUBLIC_URL || ''}/video/intro.mp4`;
    const logoSrc = `${process.env.PUBLIC_URL || ''}/img/logo.png`;

    return shell(
      videoFailed ? (
        <div className="login-intro-fallback">
          <img src={logoSrc} alt="Inclusion Carnival" className="login-intro-logo" />
        </div>
      ) : (
        <video
          ref={videoRef}
          className="login-intro-video"
          src={introSrc}
          poster={logoSrc}
          muted
          playsInline
          autoPlay
          preload="auto"
        />
      )
    );
  }

  return shell(<div className="login-loader" />);
}
